import { useState, useEffect } from 'react';
import {
	Box,
	Button,
	Flex,
	Grid,
	GridItem,
	Input,
	Text,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	useDisclosure,
	useToast,
	Heading,
	ButtonGroup,
	Skeleton,
	useColorModeValue,
} from '@chakra-ui/react';
import { z } from 'zod';
import axios from 'axios';
import globalStore from '../store';
import { formatTimestamp } from '../utility';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';
import { Platform } from '@repo/alictus-common/enums/platform';
import { CreateModal, EditModal, DeleteModal } from './addressable_modals';
import { addressableSchema } from '@repo/alictus-common/validation/addressable_schema';
import { AddressableBuildStatus, AddressableMapping } from '@repo/alictus-common/types/addressable';
import { fetchMappingData, fetchReportData, deleteMapping, createMapping, updateMapping } from './addressableApi';

type MappingFormData = z.infer<typeof addressableSchema>;

const platformLabels = {
	[Platform.iOS]: 'iOS',
	[Platform.Android]: 'Android',
	[Platform.All]: 'All',
};

function AddressableView({ userToken, selectedGameId }: { userToken: string; selectedGameId: string | null }) {
	const { JWTToken, gameId, user } = globalStore((state) => ({
		JWTToken: state.JWTToken,
		gameId: state.gameId,
		user: state.user,
	}));

	const isAdminOrProduct = user?.roles.some((role) => role === 'ADMIN' || role === 'PRODUCT');

	const [mappingData, setMappingData] = useState<AddressableMapping[]>([]);

	const [reportData, setReportData] = useState<AddressableBuildStatus[]>([]);
	const [searchQuery, setSearchQuery] = useState('');

	const [isMappingLoading, setIsMappingLoading] = useState(false);
	const [isReportLoading, setIsReportLoading] = useState(false);

	const [selectedRow, setSelectedRow] = useState<AddressableMapping | null>(null);
	const [selectedPlatformMapping, setSelectedPlatformMapping] = useState<Platform>(Platform.iOS);
	const [selectedPlatformReport, setSelectedPlatformReport] = useState<Platform>(Platform.iOS);

	const toast = useToast();

	const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();

	const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();

	const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

	const borderColor = useColorModeValue('gray.300', 'gray.600');
	

	useEffect(() => {
		const controller = new AbortController();
		const signal = controller.signal;

		const loadMapping = async () => {
			if (!JWTToken || !gameId) return;
			try {
				setIsMappingLoading(true);
				const mapping = await fetchMappingData(signal);
				setMappingData(mapping);
			} catch (err: any) {
				if (axios.isCancel(err) || err?.code === 'ERR_CANCELED') return;
				console.error('Mapping fetch error:', err);
			} finally {
				setIsMappingLoading(false);
			}
		};

		loadMapping();

		return () => controller.abort();
	}, []);

	useEffect(() => {
		const controller = new AbortController();
		const signal = controller.signal;

		const loadReports = async () => {
			if (!JWTToken || !gameId) return;
			try {
				setIsReportLoading(true);
				const reports = await fetchReportData(signal);
				setReportData(reports);
			} catch (err: any) {
				if (axios.isCancel(err) || err?.code === 'ERR_CANCELED') return;
				console.error('Mapping fetch error:', err);
			} finally {
				setIsReportLoading(false);
			}
		};

		loadReports();

		return () => controller.abort();
	}, []);

	useEffect(() => {
		const controller = new AbortController();
		const signal = controller.signal;

		if (!isEditOpen && !isCreateOpen && !isDeleteOpen) {
			const refresher = async () => {
				if (!JWTToken || !gameId) return;
				try {
					const mapping = await fetchMappingData(signal);
					setMappingData(mapping);
				} catch (err) {
					if (axios.isCancel(err)) {
						console.log('Mapping fetch cancelled');
					} else {
						console.error('Mapping fetch error:', err);
					}
				}
			};

			refresher();

			const interval = setInterval(() => {
				refresher();
			}, 15000);

			return () => {
				clearInterval(interval);
				controller.abort(); //
			};
		}
	}, [isEditOpen, isCreateOpen, isDeleteOpen]);

	useEffect(() => {
		const controller = new AbortController();
		const signal = controller.signal;

		if (!isEditOpen && !isCreateOpen && !isDeleteOpen) {
			const refresher = async () => {
				if (!JWTToken || !gameId) return;
				try {
					const mapping = await fetchReportData(signal);
					setReportData(mapping);
				} catch (err) {
					if (axios.isCancel(err)) {
						console.log('Addressable reports fetch cancelled');
					} else {
						console.error('Addressable reports fetch error:', err);
					}
				}
			};

			refresher();

			const interval = setInterval(() => {
				refresher();
			}, 15000);

			return () => {
				clearInterval(interval);
				controller.abort(); //
			};
		}
	}, [isEditOpen, isCreateOpen, isDeleteOpen]);

	const methods = useForm<MappingFormData>({
		resolver: zodResolver(addressableSchema),
	});

	const {
		reset,
		formState: { isDirty },
	} = methods;

	const handleCreate = async (data: MappingFormData) => {
		if (!JWTToken || !gameId) return;

		try {
			const newRow = await createMapping(JWTToken, gameId, data);

			// Add to state
			setMappingData((prev) => [...prev, newRow]);

			// Clear form
			reset();
			onCreateClose();

			// Toast success
			toast({
				title: 'Mapping created',
				status: 'success',
				duration: 2000,
				isClosable: true,
				position: 'top',
			});
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || error.message || 'Unexpected error occurred';
			// Toast error
			toast({
				title: 'Failed to create mapping',
				description: errorMessage,
				status: 'error',
				duration: 3000,
				isClosable: true,
				position: 'top',
			});
		}
	};

	const handleEdit = async (data: MappingFormData) => {
		if (!selectedRow || !JWTToken || !gameId) return; // selectedRow is the full MappingRow with `id`

		try {
			const updatedRow = await updateMapping(JWTToken, gameId, selectedRow.id, {
				...data,
				id: selectedRow.id, // Include ID in the payload
			});

			setMappingData((prev) => prev.map((row) => (row.id === selectedRow.id ? updatedRow : row)));

			setSelectedRow(null);
			reset();
			onEditClose();

			toast({
				title: 'Mapping updated',
				status: 'success',
				duration: 2000,
				isClosable: true,
				position: 'top',
			});
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || error.message || 'Unexpected error occurred';
			toast({
				title: 'Failed to update mapping',
				description: errorMessage,
				status: 'error',
				duration: 3000,
				isClosable: true,
				position: 'top',
			});
		}
	};

	const handleDelete = async () => {
		if (!selectedRow || !JWTToken || !gameId) return;

		try {
			await deleteMapping(JWTToken, gameId, selectedRow.id);

			setMappingData((prev) => prev.filter((row) => row.id !== selectedRow.id));
			setSelectedRow(null);
			onDeleteClose();

			toast({
				title: 'Mapping deleted',
				status: 'info',
				duration: 2000,
				isClosable: true,
				position: 'top',
			});
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || error.message || 'Unexpected error occurred';
			toast({
				title: 'Failed to delete mapping',
				description: errorMessage,
				status: 'error',
				duration: 3000,
				isClosable: true,
				position: 'top',
			});
		}
	};

	const filteredMappingData = mappingData.filter((row) => row.platform === selectedPlatformMapping);
	const filteredReportData = reportData
		.filter((row) => row.platform === selectedPlatformReport)
		.filter(
			(row) =>
				row.version?.toLowerCase().includes(searchQuery.toLowerCase()) ||
				row.buildNumber?.toString().includes(searchQuery) ||
				row.parentBuildNumber?.toString().includes(searchQuery) ||
				row.parentVersion?.toLowerCase().includes(searchQuery.toLowerCase()),
		);

	const openCreateModal = () => {
		reset({
			platform: undefined,
			version: '',
			buildNumber: undefined,
		});
		onCreateOpen();
	};

	const openEditModal = (row: AddressableMapping) => {
		setSelectedRow(row); // full row, not index
		reset({
			version: row.version,
			buildNumber: row.buildNumber,
			platform: row.platform,
		});
		onEditOpen();
	};

	const openDeleteModal = (row: AddressableMapping) => {
		setSelectedRow(row);
		onDeleteOpen();
	};

	return (
		<Grid
			height={'1px'}
			width={'100%'}
			templateRows="repeat(4, 1fr)"
			templateColumns="repeat(5, 1fr)"
			gap={2}
			paddingLeft={'12px'}
			paddingRight={'10px'}
		>
			<GridItem colSpan={6} padding={'10px'}></GridItem>
			<GridItem paddingLeft={'5px'} paddingRight={'5px'} colSpan={6} width="100%">
				<Box borderWidth={'2px'} borderRadius={'5px'} borderColor={borderColor} width="100%" minHeight="100%" overflowX="auto">
					<FormProvider {...methods}>
						<Flex direction="row" align="flex-start" gap="4" mt={12}>
							<Box p={6} flex={1} borderWidth="1px" borderRadius="xl" boxShadow="md" ml={{ base: 0, md: 4 }}>
								<Flex justify="space-between" align="center" mb={4}>
									<Flex align="center" gap={4}>
										<Heading size="md">Addressables Mapping</Heading>
										<ButtonGroup isAttached variant="outline">
											<Button
												colorScheme={selectedPlatformMapping === Platform.iOS ? 'teal' : 'gray'}
												onClick={() => setSelectedPlatformMapping(Platform.iOS)}
											>
												iOS
											</Button>
											<Button
												colorScheme={selectedPlatformMapping === Platform.Android ? 'teal' : 'gray'}
												onClick={() => setSelectedPlatformMapping(Platform.Android)}
											>
												Android
											</Button>
										</ButtonGroup>
									</Flex>
									<Button colorScheme="blue" onClick={() => openCreateModal()} isDisabled={!isAdminOrProduct}>
										Create
									</Button>
								</Flex>
								{isMappingLoading ? (
									<Skeleton height="200px" />
								) : (
									<Table variant="simple" mb={10}>
										<Thead>
											<Tr>
												<Th>Platform</Th>
												<Th>Version</Th>
												<Th>Build Number</Th>
												<Th>Updated By</Th>
												<Th>Last Updated</Th>
											</Tr>
										</Thead>
										<Tbody>
											{filteredMappingData.map((row) => (
												<Tr key={row.id}>
													<Td>{platformLabels[row.platform]}</Td>
													<Td>{row.version}</Td>
													<Td>{row.buildNumber}</Td>
													<Td>{row.updatedBy}</Td>
													<Td>
														{' '}
														<Text whiteSpace="nowrap">{formatTimestamp(row.lastUpdated)}</Text>
													</Td>
													<Td>
														<Flex direction="row" wrap="nowrap" gap={2}>
															<Button size="sm" onClick={() => openEditModal(row)} isDisabled={!isAdminOrProduct}>
																Update
															</Button>
															<Button size="sm" colorScheme="red" onClick={() => openDeleteModal(row)} isDisabled={!isAdminOrProduct}>
																Delete
															</Button>
														</Flex>
													</Td>
												</Tr>
											))}
										</Tbody>
									</Table>
								)}
							</Box>
							<Box borderWidth="1px" borderRadius="xl" boxShadow="md" p={6} flex={1} mr={{ base: 0, md: 4 }}>
								<Flex direction="column" gap={4} mb={4}>
									<Flex align="center" gap={4}>
										<Heading size="md">Addessables Build Reports</Heading>

										<ButtonGroup isAttached variant="outline">
											<Button
												colorScheme={selectedPlatformReport === Platform.iOS ? 'teal' : 'gray'}
												onClick={() => setSelectedPlatformReport(Platform.iOS)}
											>
												iOS
											</Button>
											<Button
												colorScheme={selectedPlatformReport === Platform.Android ? 'teal' : 'gray'}
												onClick={() => setSelectedPlatformReport(Platform.Android)}
											>
												Android
											</Button>
										</ButtonGroup>
									</Flex>

									<Input
										placeholder="Search version or build"
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										width="250px"
									/>
								</Flex>
								{isReportLoading ? (
									<Skeleton height="200px" />
								) : (
									<Table variant="simple">
										<Thead>
											<Tr>
												<Th>Platform</Th>
												<Th>Version</Th>
												<Th>Build</Th>
												<Th>Clean Build</Th>
												<Th>Parent Version</Th>
												<Th>Parent Build Number</Th>
												<Th>Last Updated</Th>
											</Tr>
										</Thead>
										<Tbody>
											{filteredReportData.map((row) => (
												<Tr key={row.id}>
													<Td>{platformLabels[row.platform]}</Td>
													<Td>{row.version}</Td>
													<Td>{row.buildNumber}</Td>
													<Td>{row.isCleanBuild ? 'Yes' : 'No'}</Td>
													<Td>{row.parentVersion ? row.parentVersion : '-'}</Td>
													<Td>{row.parentBuildNumber ? row.parentBuildNumber : '-'}</Td>
													<Td>
														{' '}
														<Text whiteSpace="nowrap">{formatTimestamp(row.lastUpdated)}</Text>
													</Td>
												</Tr>
											))}
										</Tbody>
									</Table>
								)}

								{/* Create Modal */}
								{userToken && selectedGameId && isCreateOpen && (
									<CreateModal isOpen={isCreateOpen} onClose={onCreateClose} onSubmit={handleCreate} />
								)}
								{/* Edit Modal */}
								{userToken && selectedGameId && isEditOpen && (
									<EditModal isOpen={isEditOpen} onClose={onEditClose} onSubmit={handleEdit} isDirty={isDirty} />
								)}
								{/* Delete Modal */}
								{userToken && selectedGameId && isDeleteOpen && (
									<DeleteModal isOpen={isDeleteOpen} onClose={onDeleteClose} onDelete={handleDelete} />
								)}
							</Box>
						</Flex>
					</FormProvider>
				</Box>
			</GridItem>
		</Grid>
	);
}

export default AddressableView;
